import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "@marekl/adminer/datetime";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, ApiError } from "../api";
import type { MusicSource } from "./music";
import type { ID } from "../id";

/**
 * Rule sets (Modul 3 — pravidla a konfigurace).
 *
 * Everything the module serves lives under the `/rules` prefix. The
 * workspace endpoint returns the whole tree of one rule set in a single
 * fetch, so the detail screen holds one query and every mutation just
 * invalidates it.
 */

export type RuleSetStatus = "draft" | "active" | "archived";
export type AgeMode = "fixed_date" | "at_competition";
export type AgeAggregation = "own" | "oldest" | "average";
// Re-exported, not redeclared: a second copy of the union drifted from
// the first the moment a value was added to one of them.
export type { MusicSource };
export type ResultFormula = "average" | "sum";
export type DeductionUnit = "occurrence" | "second";

export interface RuleSet {
  id: ID;
  name: string;
  description: string;
  season_id?: ID | null;
  status: RuleSetStatus;
  /** Music validation (Modul 6) — one common setting for the whole set.
   * Tolerance is the grace band around a combination's time limit; zero
   * on either minimum switches that check off. */
  music_tolerance_seconds: number;
  music_min_bitrate_kbps: number;
  music_min_sample_rate_hz: number;
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface RuleSetSummary extends RuleSet {
  discipline_count: number;
  /** See RuleSetWorkspace.in_service. */
  in_service: boolean;
}

export interface AgeCategory {
  id: ID;
  age_category_table_id: ID;
  code: string;
  name: string;
  min_age?: number;
  max_age?: number;
  /** A palette slot ("age-3") or "" for none. Not a hex — the rule set
   * picks FROM the design system, so the badge stays part of one family.
   * Rendered only through components/AgeBadge. */
  color: string;
  sort_order: number;
}

export interface AgeCategoryTable {
  id: ID;
  rule_set_id: ID;
  name: string;
  age_mode: AgeMode;
  ref_month: number;
  ref_day: number;
  categories?: AgeCategory[];
}

export interface FormationSize {
  id: ID;
  formation_table_id: ID;
  code: string;
  name: string;
  min_count: number;
  max_count?: number;
  age_aggregation: AgeAggregation;
  sort_order: number;
}

export interface FormationTable {
  id: ID;
  rule_set_id: ID;
  name: string;
  sizes?: FormationSize[];
}

/**
 * A performance tier (výkonnostní třída). Tiers carry no rules of their
 * own — what a tier means lives on the combinations it takes part in.
 */
export interface PerformanceClass {
  id: ID;
  performance_class_table_id: ID;
  code: string;
  name: string;
  sort_order: number;
}

export interface PerformanceClassTable {
  id: ID;
  rule_set_id: ID;
  name: string;
  classes?: PerformanceClass[];
}

export interface DeductionItem {
  id: ID;
  deduction_table_id: ID;
  name: string;
  description: string;
  points_cp: number;
  unit: DeductionUnit;
  sort_order: number;
}

export interface DeductionTable {
  id: ID;
  rule_set_id: ID;
  name: string;
  items?: DeductionItem[];
}

export interface ScoringArea {
  id: ID;
  scoring_sheet_id: ID;
  name: string;
  description: string;
  max_points_cp: number;
  step_cp: number;
  sort_order: number;
}

export interface ScoringSheet {
  id: ID;
  rule_set_id: ID;
  name: string;
  description: string;
  areas?: ScoringArea[];
}

/**
 * One competition surface. Dimensions are optional: "celá plocha" is a
 * real ČNSMS value with no fixed size.
 */
export interface Floor {
  id: ID;
  rule_set_id: ID;
  name: string;
  width_m?: number;
  length_m?: number;
  description: string;
  sort_order: number;
}

/**
 * One official ČNSMS track a rule set holds — a component like a floor,
 * referenced by disciplines as a default and by combinations as an
 * override. TD Twirl is why it is a row: duo/trio and tým are danced to
 * different recordings, exactly as they already have different time
 * limits.
 *
 * The blob key never travels; the bytes come from recordingFileUrl().
 */
export interface Recording {
  id: ID;
  rule_set_id: ID;
  name: string;
  sort_order: number;
  original_name: string;
  size_bytes: number;
  duration_ms: number;
  bitrate_kbps: number;
  sample_rate_hz: number;
  /** When the bytes were last written. Named like every other component
   * row rather than `uploaded_at`, because a recording IS a row — the
   * workspace serialises it straight from models.Recording. */
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface Discipline {
  id: ID;
  rule_set_id: ID;
  family: string;
  code: string;
  name: string;
  description: string;
  apparatus: string;
  music_source: MusicSource;
  restrictions: string;
  age_category_table_id: ID;
  formation_table_id: ID;
  performance_class_table_id: ID;
  default_deduction_table_id?: ID;
  default_scoring_sheet_id?: ID;
  default_time_min_seconds?: number;
  default_time_max_seconds?: number;
  default_floor_id?: ID;
  default_recording_id?: ID;
  default_plus_judges: number;
  default_tech_judges: number;
  default_result_formula: ResultFormula;
  sort_order: number;
  categories?: DisciplineCategory[];
}

/**
 * One combination of a discipline, an age category and a formation
 * size. Every override field is optional: absent means "inherit the
 * discipline default".
 */
export interface DisciplineCategory {
  id: ID;
  discipline_id: ID;
  age_category_id: ID;
  formation_size_id: ID;
  performance_class_id: ID;
  enabled: boolean;
  deduction_table_id?: ID;
  scoring_sheet_id?: ID;
  time_min_seconds?: number;
  time_max_seconds?: number;
  floor_id?: ID;
  recording_id?: ID;
  plus_judges?: number;
  tech_judges?: number;
  result_formula?: ResultFormula;
  note: string;
}

/** A named discipline family — a family without a row shows its code. */
export interface DisciplineFamily {
  id: ID;
  rule_set_id: ID;
  code: string;
  name: string;
  /** First key of the canonical category order. Rule-set data: the
   * federation decides which skupina disciplín comes first by ordering
   * these, and no code anywhere names MD or PPD. */
  sort_order: number;
}

/** Display name of a family: the set name, falling back to the code. */
export function familyName(
  families: DisciplineFamily[] | undefined,
  code: string,
): string {
  return families?.find((f) => f.code === code)?.name ?? code;
}

/** The whole editable contents of one rule set. */
export interface RuleSetWorkspace {
  ruleset: RuleSet;
  /**
   * A competition on this set has left draft, so its STRUCTURE is frozen:
   * which combinations exist can no longer change. Values (time limits,
   * tables, recordings, names) stay editable throughout.
   *
   * A sibling of `ruleset` rather than a field on it, deliberately: on the
   * model it would default to false — "unlocked" — in any response that
   * forgot to fill it, which is the silent wrong answer this lock exists to
   * remove. Mirror of rules.requireStructureEditable; the server refuses
   * regardless, this only stops the page offering the button.
   */
  in_service: boolean;
  /**
   * The competitions that froze it. Empty when `in_service` is false.
   *
   * The banner names them: „the set is in service" leaves an admin
   * guessing which of a season's events did it, and that answer decides
   * the next move. A set is claimed by its competitions, not by a season.
   */
  locked_by: { id: ID; name: string }[];
  age_tables: AgeCategoryTable[];
  formation_tables: FormationTable[];
  performance_class_tables: PerformanceClassTable[];
  deduction_tables: DeductionTable[];
  scoring_sheets: ScoringSheet[];
  floors: Floor[];
  /** The official ČNSMS tracks this set holds (Modul 6). */
  recordings: Recording[];
  families: DisciplineFamily[];
  disciplines: Discipline[];
}

/** A combination with its overrides folded into the discipline defaults. */
export interface ResolvedCategory {
  deduction_table_id?: ID;
  scoring_sheet_id?: ID;
  time_min_seconds: number | null;
  time_max_seconds: number | null;
  floor_id?: ID;
  recording_id?: ID;
  plus_judges: number;
  tech_judges: number;
  result_formula: ResultFormula;
  note: string;
}

/** One technical judge at most for now (rules.MaxTechJudges). */
export const MAX_TECH_JUDGES = 1;

/**
 * The eight override fields a combination may reset back to the
 * discipline default. Kept in step with the backend's clearable list.
 */
export const CLEARABLE_CATEGORY_FIELDS = [
  "deduction_table_id",
  "scoring_sheet_id",
  "time_min_seconds",
  "time_max_seconds",
  "floor_id",
  "recording_id",
  "plus_judges",
  "tech_judges",
  "result_formula",
] as const;

export type ClearableCategoryField = (typeof CLEARABLE_CATEGORY_FIELDS)[number];

/**
 * Mirrors the backend's two-level resolution so the matrix can show what
 * a combination actually means without a round trip. An override of the
 * zero value wins over the default — a zero minimum time is "no
 * minimum", not "unset".
 */
export function resolveCategory(d: Discipline, c: DisciplineCategory): ResolvedCategory {
  const pick = <T,>(override: T | undefined, fallback: T): T =>
    override === undefined ? fallback : override;
  return {
    deduction_table_id: pick(c.deduction_table_id, d.default_deduction_table_id),
    scoring_sheet_id: pick(c.scoring_sheet_id, d.default_scoring_sheet_id),
    time_min_seconds: pick(c.time_min_seconds, d.default_time_min_seconds) ?? null,
    time_max_seconds: pick(c.time_max_seconds, d.default_time_max_seconds) ?? null,
    floor_id: pick(c.floor_id, d.default_floor_id),
    recording_id: pick(c.recording_id, d.default_recording_id),
    plus_judges: pick(c.plus_judges, d.default_plus_judges),
    tech_judges: pick(c.tech_judges, d.default_tech_judges),
    result_formula: pick(c.result_formula, d.default_result_formula),
    note: c.note,
  };
}

/**
 * How a surface reads in the UI. The dimensions are shown next to the
 * name rather than instead of it, because the names carry meaning the
 * numbers do not ("celá plocha" has no numbers at all).
 */
export function floorLabel(f: Floor): string {
  if (f.width_m == null || f.length_m == null) return f.name;
  const size = `${f.width_m} × ${f.length_m} m`;
  return f.name === size ? f.name : `${f.name} (${size})`;
}

// --- payloads ---

export interface RuleSetPayload {
  name: string;
  description?: string;
  season_id?: ID | null;
  /** Optional on the wire: an absent key leaves the stored value alone,
   * because zero means something for all three. */
  music_tolerance_seconds?: number;
  music_min_bitrate_kbps?: number;
  music_min_sample_rate_hz?: number;
}

export interface DuplicatePayload {
  name: string;
  season_id?: ID | null;
}

export interface AgeTablePayload {
  name: string;
  age_mode?: AgeMode;
  ref_month?: number;
  ref_day?: number;
}

export interface AgeCategoryPayload {
  code: string;
  name: string;
  min_age?: number | null;
  max_age?: number | null;
  /** A palette slot, or "" to clear it. The server refuses anything else. */
  color?: string;
  sort_order?: number;
}

export interface FormationTablePayload {
  name: string;
}

export interface ClassTablePayload {
  name: string;
}

export interface PerformanceClassPayload {
  code: string;
  name: string;
  sort_order?: number;
}

export interface FormationSizePayload {
  code: string;
  name: string;
  min_count: number;
  max_count?: number | null;
  age_aggregation?: AgeAggregation;
  sort_order?: number;
}

export interface DeductionTablePayload {
  name: string;
}

export interface DeductionItemPayload {
  name: string;
  description?: string;
  points_cp: number;
  unit?: DeductionUnit;
  sort_order?: number;
}

export interface ScoringSheetPayload {
  name: string;
  description?: string;
}

export interface ScoringAreaPayload {
  name: string;
  description?: string;
  max_points_cp: number;
  step_cp?: number;
  sort_order?: number;
}

export interface FloorPayload {
  name: string;
  width_m?: number | null;
  length_m?: number | null;
  description?: string;
  sort_order?: number;
}

export interface RecordingPayload {
  name: string;
  sort_order?: number;
}

export interface DisciplinePayload {
  family: string;
  code: string;
  name: string;
  description?: string;
  apparatus?: string;
  music_source?: MusicSource;
  restrictions?: string;
  age_category_table_id: ID;
  formation_table_id: ID;
  performance_class_table_id: ID;
  default_deduction_table_id?: ID | null;
  default_scoring_sheet_id?: ID | null;
  default_time_min_seconds?: number | null;
  default_time_max_seconds?: number | null;
  default_floor_id?: ID | null;
  default_recording_id?: ID | null;
  default_plus_judges?: number;
  default_tech_judges?: number;
  default_result_formula?: ResultFormula;
  sort_order?: number;
}

/**
 * The single combination write. `set` carries new values, `clear` names
 * fields to reset to the discipline default, and anything in neither is
 * left untouched — which is what makes editing one field across a whole
 * row safe when the combinations differ in every other field.
 */
export interface CategoriesBulkPayload {
  category_ids: ID[];
  enabled?: boolean;
  clear?: ClearableCategoryField[];
  set?: {
    deduction_table_id?: ID;
    scoring_sheet_id?: ID;
    time_min_seconds?: number;
    time_max_seconds?: number;
    floor_id?: ID;
    recording_id?: ID;
    plus_judges?: number;
    tech_judges?: number;
    result_formula?: ResultFormula;
    note?: string;
  };
}

// --- queries ---

export function useRuleSets() {
  return useQuery<RuleSetSummary[]>({
    queryKey: ["admin", "rulesets"],
    queryFn: async () => {
      const res = await apiGet<{ data: RuleSetSummary[] }>("/rules/rulesets");
      return res.data;
    },
  });
}

// --- published document (what Modul 5 consumes) ---

/** One competable combination, resolved against the discipline defaults. */
export interface DocumentCategory {
  id: ID;
  age_category_id: ID;
  formation_size_id: ID;
  performance_class_id: ID;
  deduction_table_id?: ID;
  scoring_sheet_id?: ID;
  time_min_seconds?: number | null;
  time_max_seconds?: number | null;
  floor_id?: ID;
  /** The official ČNSMS track this combination is danced to, resolved
   * combination-override-else-discipline-default like every other
   * component. Only meaningful when the discipline's `music_source` is
   * `federation`; look it up in the document's `recordings`. */
  recording_id?: ID;
  plus_judges: number;
  tech_judges: number;
  result_formula: ResultFormula;
  note?: string;
}

export interface DocumentDiscipline {
  id: ID;
  family: string;
  code: string;
  name: string;
  description?: string;
  apparatus?: string;
  music_source: MusicSource;
  restrictions?: string;
  parent_discipline_id?: ID;
  age_category_table_id: ID;
  formation_table_id: ID;
  performance_class_table_id: ID;
  categories: DocumentCategory[];
}

/** The published, enabled-only, resolved form of a rule set. */
export interface RuleSetDocument {
  ruleset: RuleSet;
  age_tables: AgeCategoryTable[];
  formation_tables: FormationTable[];
  performance_class_tables: PerformanceClassTable[];
  deduction_tables: DeductionTable[];
  scoring_sheets: ScoringSheet[];
  /** The official ČNSMS tracks, referenced by `recording_id` from the
   * combinations. */
  recordings: Recording[];
  families: DisciplineFamily[];
  disciplines: DocumentDiscipline[];
}

export function useRuleSetDocument(id: ID | undefined) {
  return useQuery<RuleSetDocument>({
    queryKey: ["rules", "document", id],
    enabled: !!id,
    queryFn: () => apiGet<RuleSetDocument>(`/rules/rulesets/${id}/document`),
  });
}

export function useRuleSet(id: ID | undefined) {
  return useQuery<RuleSetWorkspace>({
    queryKey: ["admin", "rulesets", id],
    queryFn: () => apiGet<RuleSetWorkspace>(`/rules/rulesets/${id}`),
    enabled: !!id,
  });
}

/**
 * Wraps a rule-set write with the one invalidation every one of them
 * needs. The prefix covers both the list and each detail: a write can
 * change a discipline count in the list as easily as the tree, and the
 * payloads are small enough that a narrower key would only buy
 * complexity.
 */
function useRulesMutation<TData, TVars>(mutationFn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn,
    // The dialogs in this workspace render their own refusal: the edit forms
    // in an Alert above the fields, the deletes in the confirm dialog's error
    // slot. The global toast would say it a second time, and for a delete it
    // would say it from behind the dialog still asking the question.
    //
    // The two call sites with NO dialog on screen — „Duplikovat" and the
    // reorder arrows, both fired from the card — therefore pass their own
    // `onError`. Suppressing here without them is what made a failed reorder
    // silent; if you add another card-level mutation, give it one too.
    meta: { suppressErrorToast: true },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "rulesets"] }),
        // The published document caches separately — a family rename or
        // any rules edit must reach the wizard within the stale window.
        qc.invalidateQueries({ queryKey: ["rules", "document"] }),
      ]),
  });
}

/**
 * Writes the disciplines' running order — the second key of the
 * canonical category order.
 *
 * Its own endpoint rather than a `sort_order` on the update payload,
 * because an update rebuilds the whole discipline: moving one row would
 * mean re-sending all nineteen writable fields, and anything the caller
 * forgot would be blanked. Sends the complete order, which the server
 * checks is a permutation before it renumbers anything.
 */
export function useReorderDisciplines(ruleSetId: ID) {
  return useRulesMutation<void, { ids: ID[] }>((body) =>
    apiPut(`/rules/rulesets/${ruleSetId}/disciplines/order`, body),
  );
}

/**
 * Writes the editable parts of one discipline family (upsert by code):
 * its display name and its place in the canonical category order.
 *
 * `sort_order` is optional on the wire — omitted, the server leaves the
 * position alone, so a rename does not have to know where the family
 * sits and a reorder does not have to know its name.
 */
export function useSetFamily(ruleSetId: ID) {
  return useRulesMutation<
    DisciplineFamily,
    { code: string; name: string; sort_order?: number }
  >(({ code, ...body }) =>
    apiPut(`/rules/rulesets/${ruleSetId}/families/${encodeURIComponent(code)}`, body),
  );
}

// --- rule sets ---

export function useCreateRuleSet() {
  return useRulesMutation<RuleSet, RuleSetPayload>((p) => apiPost("/rules/rulesets", p));
}

export function useUpdateRuleSet(id: ID) {
  return useRulesMutation<RuleSet, RuleSetPayload>((p) => apiPatch(`/rules/rulesets/${id}`, p));
}

export function useDeleteRuleSet() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/rulesets/${id}`));
}

export function useDuplicateRuleSet(id: ID) {
  return useRulesMutation<RuleSet, DuplicatePayload>((p) =>
    apiPost(`/rules/rulesets/${id}/duplicate`, p),
  );
}

export function useSetRuleSetStatus(id: ID) {
  return useRulesMutation<RuleSet, RuleSetStatus>((status) =>
    apiPost(`/rules/rulesets/${id}/status`, { status }),
  );
}

// --- age category tables ---

export function useCreateAgeTable(ruleSetId: ID) {
  return useRulesMutation<AgeCategoryTable, AgeTablePayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/age-tables`, p),
  );
}

export function useUpdateAgeTable() {
  return useRulesMutation<AgeCategoryTable, { id: ID; payload: AgeTablePayload }>(
    ({ id, payload }) => apiPatch(`/rules/age-tables/${id}`, payload),
  );
}

export function useDeleteAgeTable() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/age-tables/${id}`));
}

export function useCreateAgeCategory() {
  return useRulesMutation<AgeCategory, { tableId: ID; payload: AgeCategoryPayload }>(
    ({ tableId, payload }) => apiPost(`/rules/age-tables/${tableId}/categories`, payload),
  );
}

export function useUpdateAgeCategory() {
  return useRulesMutation<AgeCategory, { id: ID; payload: AgeCategoryPayload }>(
    ({ id, payload }) => apiPatch(`/rules/age-categories/${id}`, payload),
  );
}

export function useDeleteAgeCategory() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/age-categories/${id}`));
}

// --- formation tables ---

export function useCreateFormationTable(ruleSetId: ID) {
  return useRulesMutation<FormationTable, FormationTablePayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/formation-tables`, p),
  );
}

export function useUpdateFormationTable() {
  return useRulesMutation<FormationTable, { id: ID; payload: FormationTablePayload }>(
    ({ id, payload }) => apiPatch(`/rules/formation-tables/${id}`, payload),
  );
}

export function useDeleteFormationTable() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/formation-tables/${id}`));
}

export function useCreateFormationSize() {
  return useRulesMutation<FormationSize, { tableId: ID; payload: FormationSizePayload }>(
    ({ tableId, payload }) => apiPost(`/rules/formation-tables/${tableId}/sizes`, payload),
  );
}

export function useUpdateFormationSize() {
  return useRulesMutation<FormationSize, { id: ID; payload: FormationSizePayload }>(
    ({ id, payload }) => apiPatch(`/rules/formation-sizes/${id}`, payload),
  );
}

export function useDeleteFormationSize() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/formation-sizes/${id}`));
}

// --- performance class tables ---

export function useCreateClassTable(ruleSetId: ID) {
  return useRulesMutation<PerformanceClassTable, ClassTablePayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/class-tables`, p),
  );
}

export function useUpdateClassTable() {
  return useRulesMutation<PerformanceClassTable, { id: ID; payload: ClassTablePayload }>(
    ({ id, payload }) => apiPatch(`/rules/class-tables/${id}`, payload),
  );
}

export function useDeleteClassTable() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/class-tables/${id}`));
}

export function useCreatePerformanceClass() {
  return useRulesMutation<
    PerformanceClass,
    { tableId: ID; payload: PerformanceClassPayload }
  >(({ tableId, payload }) => apiPost(`/rules/class-tables/${tableId}/classes`, payload));
}

export function useUpdatePerformanceClass() {
  return useRulesMutation<PerformanceClass, { id: ID; payload: PerformanceClassPayload }>(
    ({ id, payload }) => apiPatch(`/rules/performance-classes/${id}`, payload),
  );
}

export function useDeletePerformanceClass() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/performance-classes/${id}`));
}

// --- deduction tables ---

export function useCreateDeductionTable(ruleSetId: ID) {
  return useRulesMutation<DeductionTable, DeductionTablePayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/deduction-tables`, p),
  );
}

export function useUpdateDeductionTable() {
  return useRulesMutation<DeductionTable, { id: ID; payload: DeductionTablePayload }>(
    ({ id, payload }) => apiPatch(`/rules/deduction-tables/${id}`, payload),
  );
}

export function useDeleteDeductionTable() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/deduction-tables/${id}`));
}

export function useCreateDeductionItem() {
  return useRulesMutation<DeductionItem, { tableId: ID; payload: DeductionItemPayload }>(
    ({ tableId, payload }) => apiPost(`/rules/deduction-tables/${tableId}/items`, payload),
  );
}

export function useUpdateDeductionItem() {
  return useRulesMutation<DeductionItem, { id: ID; payload: DeductionItemPayload }>(
    ({ id, payload }) => apiPatch(`/rules/deduction-items/${id}`, payload),
  );
}

export function useDeleteDeductionItem() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/deduction-items/${id}`));
}

// --- duplicating one component or discipline ---

/**
 * Copying a table or a discipline inside its own rule set — distinct
 * from DuplicatePayload, which copies a whole set into a new one. The
 * name is optional: left out, the backend picks the first free
 * "… (kopie)", so the common case needs no dialog.
 */
export interface DuplicateOnePayload {
  name?: string;
}

function useDuplicate<T>(path: (id: ID) => string) {
  return useRulesMutation<T, { id: ID; payload?: DuplicateOnePayload }>(({ id, payload }) =>
    apiPost(path(id), payload ?? {}),
  );
}

export const useDuplicateAgeTable = () =>
  useDuplicate<AgeCategoryTable>((id) => `/rules/age-tables/${id}/duplicate`);

export const useDuplicateFormationTable = () =>
  useDuplicate<FormationTable>((id) => `/rules/formation-tables/${id}/duplicate`);

export const useDuplicateClassTable = () =>
  useDuplicate<PerformanceClassTable>((id) => `/rules/class-tables/${id}/duplicate`);

export const useDuplicateDeductionTable = () =>
  useDuplicate<DeductionTable>((id) => `/rules/deduction-tables/${id}/duplicate`);

export const useDuplicateScoringSheet = () =>
  useDuplicate<ScoringSheet>((id) => `/rules/scoring-sheets/${id}/duplicate`);

/** A discipline copy may also be given a code; both are optional. */
export function useDuplicateDiscipline() {
  return useRulesMutation<Discipline, { id: ID; payload?: { name?: string; code?: string } }>(
    ({ id, payload }) => apiPost(`/rules/disciplines/${id}/duplicate`, payload ?? {}),
  );
}

// --- competition floors ---

export function useCreateFloor(ruleSetId: ID) {
  return useRulesMutation<Floor, FloorPayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/floors`, p),
  );
}

export function useUpdateFloor() {
  return useRulesMutation<Floor, { id: ID; payload: FloorPayload }>(({ id, payload }) =>
    apiPatch(`/rules/floors/${id}`, payload),
  );
}

export function useDeleteFloor() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/floors/${id}`));
}

// --- official ČNSMS recordings ---
//
// Only the rename is a JSON PATCH. Creating one and swapping its file
// post the MP3 as a raw body, like every other upload in Modul 6, so
// they live next to the other music hooks in queries/music.ts.

export function useRenameRecording() {
  return useRulesMutation<Recording, { id: ID; payload: RecordingPayload }>(
    ({ id, payload }) => apiPatch(`/rules/recordings/${id}`, payload),
  );
}

export function useDeleteRecording() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/recordings/${id}`));
}

// --- scoring sheets ---

export function useCreateScoringSheet(ruleSetId: ID) {
  return useRulesMutation<ScoringSheet, ScoringSheetPayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/scoring-sheets`, p),
  );
}

export function useUpdateScoringSheet() {
  return useRulesMutation<ScoringSheet, { id: ID; payload: ScoringSheetPayload }>(
    ({ id, payload }) => apiPatch(`/rules/scoring-sheets/${id}`, payload),
  );
}

export function useDeleteScoringSheet() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/scoring-sheets/${id}`));
}

export function useCreateScoringArea() {
  return useRulesMutation<ScoringArea, { sheetId: ID; payload: ScoringAreaPayload }>(
    ({ sheetId, payload }) => apiPost(`/rules/scoring-sheets/${sheetId}/areas`, payload),
  );
}

export function useUpdateScoringArea() {
  return useRulesMutation<ScoringArea, { id: ID; payload: ScoringAreaPayload }>(
    ({ id, payload }) => apiPatch(`/rules/scoring-areas/${id}`, payload),
  );
}

export function useDeleteScoringArea() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/scoring-areas/${id}`));
}

// --- disciplines ---

export function useCreateDiscipline(ruleSetId: ID) {
  return useRulesMutation<Discipline, DisciplinePayload>((p) =>
    apiPost(`/rules/rulesets/${ruleSetId}/disciplines`, p),
  );
}

export function useUpdateDiscipline() {
  return useRulesMutation<Discipline, { id: ID; payload: DisciplinePayload }>(
    ({ id, payload }) => apiPatch(`/rules/disciplines/${id}`, payload),
  );
}

export function useDeleteDiscipline() {
  return useRulesMutation<void, ID>((id) => apiDelete(`/rules/disciplines/${id}`));
}

export function useUpdateDisciplineCategories(disciplineId: ID) {
  return useRulesMutation<{ data: DisciplineCategory[] }, CategoriesBulkPayload>((p) =>
    apiPost(`/rules/disciplines/${disciplineId}/categories/bulk`, p),
  );
}
